import { useState, useEffect } from 'react';
import { Layout } from 'react-grid-layout';

interface ThemePreferences {
  density: 'compact' | 'comfortable' | 'spacious';
  fontSize: number;
  accentColor: string;
  borderRadius: number;
}

interface AdminPreferences {
  dashboardLayout?: Layout[];
  theme: ThemePreferences;
  lastUpdated: string;
}

const DEFAULT_PREFERENCES: AdminPreferences = {
  theme: {
    density: 'comfortable',
    fontSize: 15,
    accentColor: '220 90% 56%',
    borderRadius: 0.75,
  },
  lastUpdated: new Date().toISOString(),
};

export const useAdminPreferences = () => {
  const [preferences, setPreferences] = useState<AdminPreferences>(DEFAULT_PREFERENCES);
  const [isLoading, setIsLoading] = useState(true);

  // Load preferences from localStorage on mount
  useEffect(() => {
    const loadPreferences = () => {
      try {
        const stored = localStorage.getItem('admin_preferences');
        if (stored) {
          const parsed = JSON.parse(stored);
          setPreferences({ ...DEFAULT_PREFERENCES, ...parsed });
        }
      } catch (error) {
        console.error('Error loading admin preferences:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPreferences();
  }, []);

  // Save preferences to localStorage whenever they change
  const savePreferences = (newPreferences: Partial<AdminPreferences>) => {
    const updated = {
      ...preferences,
      ...newPreferences,
      lastUpdated: new Date().toISOString(),
    };

    setPreferences(updated);
    
    try {
      localStorage.setItem('admin_preferences', JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving admin preferences:', error);
    }
  };

  const updateTheme = (theme: Partial<ThemePreferences>) => {
    savePreferences({
      theme: { ...preferences.theme, ...theme },
    });
  };

  const updateDashboardLayout = (layout: Layout[]) => {
    savePreferences({ dashboardLayout: layout });
  };

  const resetPreferences = () => {
    setPreferences(DEFAULT_PREFERENCES);
    localStorage.removeItem('admin_preferences');
  };

  return {
    preferences,
    isLoading,
    updateTheme,
    updateDashboardLayout,
    resetPreferences,
    savePreferences,
  };
};
